<template>
  <div class="content-card">
    <div class="content-title">安全中心</div>
    <el-card class="security-card">
      <el-form
          :model="pwdForm"
          :rules="pwdRules"
          ref="pwdFormRef"
          label-width="12rem"
          class="pwd-form"
      >
        <el-form-item label="旧密码" prop="oldPassword">
          <el-input
              v-model="pwdForm.oldPassword"
              type="password"
              placeholder="请输入旧密码"
              :style="{width: '40rem'}"
          ></el-input>
        </el-form-item>

        <el-form-item label="新密码" prop="newPassword">
          <el-input
              v-model="pwdForm.newPassword"
              type="password"
              placeholder="请输入新密码（8-20位，包含大小写字母和特殊符号）"
              :style="{width: '40rem'}"
              @input="checkPasswordStrength"
          ></el-input>

          <!-- 密码强度指示器 -->
          <div class="password-strength" v-if="pwdForm.newPassword">
            <div class="strength-label">密码强度：{{ strengthText }}</div>
            <div class="strength-bars">
              <div
                  class="strength-bar"
                  :class="['bar-1', strength >= 1 ? 'active' : '']"
              ></div>
              <div
                  class="strength-bar"
                  :class="['bar-2', strength >= 2 ? 'active' : '']"
              ></div>
              <div
                  class="strength-bar"
                  :class="['bar-3', strength >= 3 ? 'active' : '']"
              ></div>
            </div>
            <div class="strength-hint" v-if="strengthHint">{{ strengthHint }}</div>
          </div>
        </el-form-item>

        <el-form-item label="确认新密码" prop="confirmPwd">
          <el-input
              v-model="pwdForm.confirmPwd"
              type="password"
              placeholder="请再次输入新密码"
              :style="{width: '40rem'}"
          ></el-input>
        </el-form-item>

        <el-form-item>
          <el-button
              type="primary"
              @click="handlePwdSubmit"
              class="submit-btn"
          >
            确认修改
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import newService from "@/api/newService";

export default {
  name: "SecurityCenter",
  data() {
    // 密码强度校验
    const validatePasswordStrength = (rule, value, callback) => {
      if (!value) {
        return callback(new Error('请输入密码'));
      }
      if (value.length < 8) {
        return callback(new Error('密码长度不能少于8位'));
      }
      const hasUpperCase = /[A-Z]/.test(value);
      const hasLowerCase = /[a-z]/.test(value);
      const hasNumber = /[0-9]/.test(value);
      const hasSpecialChar = /[^a-zA-Z0-9]/.test(value);

      // 密码需要包含大小写字母、数字和特殊符号中的至少三种
      const strengthCount = [hasUpperCase, hasLowerCase, hasNumber, hasSpecialChar].filter(Boolean).length;

      if (strengthCount < 3) {
        return callback(new Error('密码必须包含大小写字母、数字和特殊符号中的至少三种'));
      }
      callback();
    };

    // 确认密码校验
    const validateConfirmPwd = (rule, value, callback) => {
      if (!value) {
        return callback(new Error('请再次输入密码'));
      }
      if (value !== this.pwdForm.newPassword) {
        return callback(new Error('两次输入的密码不一致'));
      }
      callback();
    };

    return {
      // 密码修改表单数据
      pwdForm: {
        oldPassword: "",
        newPassword: "",
        confirmPwd: ""
      },
      // 密码强度相关
      strength: 0,
      strengthText: "",
      strengthHint: "",
      // 密码修改表单验证规则
      pwdRules: {
        oldPassword: [
          { required: true, message: "请输入旧密码", trigger: "blur" }
        ],
        newPassword: [
          { required: true, message: "请输入新密码", trigger: "blur" },
          { validator: validatePasswordStrength, trigger: "blur" }
        ],
        confirmPwd: [
          { required: true, message: "请确认新密码", trigger: "blur" },
          { validator: validateConfirmPwd, trigger: "blur" }
        ]
      }
    };
  },
  methods: {
    // 检查密码强度
    checkPasswordStrength() {
      const value = this.pwdForm.newPassword;
      if (!value) {
        this.strength = 0;
        this.strengthText = "";
        this.strengthHint = "";
        return;
      }

      // 评估密码强度
      const hasUpperCase = /[A-Z]/.test(value);
      const hasLowerCase = /[a-z]/.test(value);
      const hasNumber = /[0-9]/.test(value);
      const hasSpecialChar = /[^a-zA-Z0-9]/.test(value);
      const lengthScore = value.length >= 12 ? 1 : 0;

      // 计算强度分数
      let score = [hasUpperCase, hasLowerCase, hasNumber, hasSpecialChar].filter(Boolean).length;
      score += lengthScore;

      // 设置强度显示
      if (score <= 2) {
        this.strength = 1;
        this.strengthText = "弱";
        this.strengthHint = "建议增加密码长度并包含多种字符类型";
      } else if (score <= 4) {
        this.strength = 2;
        this.strengthText = "中";
        this.strengthHint = "还可以更好！尝试增加长度或添加特殊符号";
      } else {
        this.strength = 3;
        this.strengthText = "强";
        this.strengthHint = "很棒！这是一个安全的密码";
      }
    },

    // 提交密码修改
    handlePwdSubmit() {
      this.$refs.pwdFormRef.validate((isValid) => {
        if (isValid) {
          newService.savePwdToUser(this.pwdForm.oldPassword, this.pwdForm.newPassword).then(() => {
            // 密码修改成功，通知父组件处理后续逻辑
            this.$emit('passwordUpdated');
          }).catch((err) => {
            console.log(err);
            this.$message({
              type: "error",
              message: "密码修改失败，请检查旧密码是否正确"
            });
          });
          // 重置表单
          this.$refs.pwdFormRef.resetFields();
          this.strength = 0;
          this.strengthText = "";
          this.strengthHint = "";
        } else {
          this.$message({
            type: "error",
            message: "表单验证失败，请检查输入内容"
          });
          return false;
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.content-card {
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 0.5rem;
  box-shadow: 0 0.2rem 1rem rgba(0, 0, 0, 0.05);
  padding: 3rem;
  margin-bottom: 5rem;
}

.content-title {
  font-size: 2.4rem;
  color: #333333;
  font-weight: 500;
  margin-bottom: 3rem;
  padding-bottom: 1rem;
  border-bottom: 0.1rem solid #E5E5E5;
}

// 安全中心卡片样式
.security-card {
  border: none;
  box-shadow: none;
}

.pwd-form {
  padding-top: 2rem;

  ::v-deep .el-form-item {
    margin-bottom: 2.5rem;

    .el-form-item__label {
      font-size: 1.6rem;
      color: #333333;
    }

    .el-input {
      font-size: 1.6rem;

      ::v-deep .el-input__inner {
        height: 4rem;
        line-height: 4rem;
        border-radius: 0.3rem;
        border: 0.1rem solid #E5E5E5;

        &:focus {
          border-color: #FF0B41;
          box-shadow: 0 0 0 0.2rem rgba(255, 11, 65, 0.2);
        }
      }
    }

    .el-form-item__error {
      font-size: 1.4rem;
      margin-top: 0.5rem;
    }
  }

  .submit-btn {
    font-size: 1.6rem;
    padding: 0 2rem;
    height: 4rem;
    line-height: 4rem;
    background-color: #FF0B41;
    border-color: #FF0B41;
    border-radius: 0.3rem;

    &:hover {
      background-color: #e60a3a;
      border-color: #e60a3a;
    }
  }
}

// 密码强度样式
.password-strength {
  margin-top: 1rem;
  width: 40rem;

  .strength-label {
    font-size: 1.4rem;
    margin-bottom: 0.5rem;
    color: #666;
  }

  .strength-bars {
    display: flex;
    gap: 0.5rem;
    height: 0.8rem;
    margin-bottom: 0.5rem;
  }

  .strength-bar {
    flex: 1;
    background-color: #ddd;
    border-radius: 0.4rem;
    transition: background-color 0.3s;
  }

  .bar-1.active {
    background-color: #ff4d4f;
  }

  .bar-2.active {
    background-color: #faad14;
  }

  .bar-3.active {
    background-color: #52c41a;
  }

  .strength-hint {
    font-size: 1.3rem;
    color: #999;
  }
}
</style>
